/**
 * UTM Parameter Tracking Service
 * 
 * Captures UTM parameters from the URL query string for marketing attribution.
 * Parameters are captured once on page load and forwarded to Reeach registration URL.
 */

export interface UtmParams {
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  utm_content?: string;
  utm_term?: string;
  utm_id?: string;
}

const UTM_STORAGE_KEY = 'wizard_utm_params';

class UtmService {
  private utmParams: UtmParams | null = null;

  /**
   * Initialize the service by capturing UTM parameters from the current URL.
   */
  public init(): void {
    const stored = this.getStoredParams();
    if (stored) {
      this.utmParams = stored;
      return;
    }

    this.captureFromUrl();
  }

  /**
   * Captures UTM parameters from the current URL and stores them.
   */
  private captureFromUrl(): void {
    const searchParams = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_campaign', 'utm_medium', 'utm_content', 'utm_term', 'utm_id'];
    
    const params: UtmParams = {};
    
    utmKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value && value.trim()) {
        params[key as keyof UtmParams] = value.trim();
      }
    });

    this.utmParams = params;
    this.storeParams(params);
  }

  /**
   * Stores UTM parameters in sessionStorage.
   */
  private storeParams(params: UtmParams): void {
    try {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    } catch (e) {
      console.warn('Failed to store UTM params:', e);
    }
  }

  /**
   * Retrieves stored UTM parameters from sessionStorage.
   */
  private getStoredParams(): UtmParams | null {
    try {
      const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.warn('Failed to retrieve UTM params:', e);
      return null;
    }
  }

  /**
   * Returns the captured UTM parameters (only non-empty values).
   */
  public getUtmParams(): UtmParams {
    return this.utmParams || this.getStoredParams() || {};
  }
}

export default new UtmService();
